import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketplaceListingService } from '../../api/generated/services/MarketplaceListingService';
import type { MarketplaceCategoryListView } from '../../api/generated/models/MarketplaceCategoryListView';
import { CancelError, type CancelablePromise } from '../../api/generated/core/CancelablePromise';
import { ApiError } from '../../api/generated/core/ApiError';
import MarketplaceSyncPopconfirm from './MarketplaceSyncPopconfirm';
import { useAppDispatch } from '../../hooks/redux';
import { usePermissions } from '../../hooks/usePermissions';
import { addNotification } from '../../store/slices/notificationSlice';
import { applyContentMasterListItemMarketplaceCategory } from '../../store/slices/contentMasterSlice';

type Category = NonNullable<MarketplaceCategoryListView['categories']>[number];

export interface MarketplaceCategoryPickerProps {
  /** Channel identifier `shop:N`. */
  source: string;
  /** Article the chosen category is assigned to (assign mode only). */
  wawiArtikelId?: number | null;
  dataSourceId?: string | null;
  /**
   * Called after a category is successfully assigned (UPSERT to JTL Wawi SCX via
   * `POST /marketplace-listing/assign-category`). Assign mode only.
   */
  onAssigned?: (kCategory: number, categoryName: string) => void;
  /**
   * `'assign'` (default) - the classic flow: the row button writes the
   * category to Wawi via `POST /marketplace-listing/assign-category`.
   * `'select'` - pure picker (Content Master search filters): the row button
   * just hands the category to `onSelect` - no API call, no confirmation
   * popconfirm, no `content_master_wawi_write` requirement (filtering is
   * read-only).
   */
  mode?: 'assign' | 'select';
  /** Select-mode callback: the user picked a category. */
  onSelect?: (kCategory: number, categoryName: string) => void;
  /**
   * Optional page-fetch override. Default: SCX
   * `GET /marketplace-listing/categories`. The Content Master filter panel
   * passes `GET /content-master/categories` here for non-SCX channels (the
   * Wawi view is a structural subset of the SCX view, same q /
   * parentKCategory / offset contract). Must be referentially stable
   * (useCallback) - the load effects only re-run on source/dataSourceId.
   */
  fetchPage?: (opts: {
    q?: string;
    parentKCategory?: number;
    offset?: number;
  }) => CancelablePromise<MarketplaceCategoryListView>;
}

/**
 * localStorage key persisting the user's "don't ask again" choice for the
 * Set-category confirmation popconfirm.
 */
export const SET_CATEGORY_CONFIRM_DISMISS_KEY = 'cm_set_category_confirm_dismissed';

/** Reads the persisted "don't ask again" preference. Safe on blocked storage. */
const isSetCategoryConfirmDismissed = (): boolean => {
  try {
    return localStorage.getItem(SET_CATEGORY_CONFIRM_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

/** Persists the "don't ask again" preference. */
const persistSetCategoryConfirmDismissed = (): void => {
  try {
    localStorage.setItem(SET_CATEGORY_CONFIRM_DISMISS_KEY, '1');
  } catch {
    // Storage may be unavailable (private mode / quota). Non-fatal: the user
    // will simply be asked again next time.
  }
};

/**
 * Codes assign-category returns when the synchronous marketplace schema fetch
 * fails (T7). On ANY of them NOTHING was assigned - the picker stays open and
 * the user can simply retry.
 */
const ASSIGN_ERROR_CODES = new Set([
  'marketplace_credentials_missing',
  'marketplace_auth_failed',
  'marketplace_rate_limited',
  'marketplace_schema_fetch_failed',
]);

const SEARCH_DEBOUNCE_MS = 500;
const LIMIT = 50;
/** `parentKCategory=0` requests the top-level (root) categories. */
const ROOT_PARENT = 0;
/** Distance (px) from the bottom of the scroll area that triggers the next page. */
const SCROLL_THRESHOLD_PX = 48;

/**
 * Inline category browser + assignment. Two modes:
 * - empty search box → lazy tree: roots (`parentKCategory=0`), each node with
 *   `hasChildren` expandable on demand (`parentKCategory=<kCategory>`);
 * - non-empty search box → flat search by name/parent/id (`q`).
 * Each level is paginated (`offset`/`limit` + `hasMore`): the outer list loads
 * the next page on scroll, expanded children get a "Load more" button.
 * The category is only assigned when the user presses "Set category".
 */
const MarketplaceCategoryPicker: React.FC<MarketplaceCategoryPickerProps> = ({
  source,
  wawiArtikelId,
  dataSourceId,
  onAssigned,
  mode = 'assign',
  onSelect,
  fetchPage,
}) => {
  const isSelectMode = mode === 'select';
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // Subuser: assigning a category writes to Wawi (content_master_wawi_write).
  const { can } = usePermissions();
  const canCmWawiWrite = can('content_master_wawi_write');
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** kCategory currently being assigned (drives the per-row loading label). */
  const [assigningKey, setAssigningKey] = useState<number | null>(null);
  /** kCategory whose Set-category confirmation popconfirm is open. */
  const [confirmKey, setConfirmKey] = useState<number | null>(null);
  /** "Don't ask again" checkbox state inside the open popconfirm. */
  const [confirmDontAsk, setConfirmDontAsk] = useState(false);

  // Browse (tree) mode state.
  const [roots, setRoots] = useState<Category[]>([]);
  const [rootsLoading, setRootsLoading] = useState(false);
  const [rootsLoadingMore, setRootsLoadingMore] = useState(false);
  const [rootsHasMore, setRootsHasMore] = useState(false);
  const [childrenByParent, setChildrenByParent] = useState<Record<number, Category[]>>({});
  const [childHasMore, setChildHasMore] = useState<Record<number, boolean>>({});
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  const [nodeLoading, setNodeLoading] = useState<Set<number>>(() => new Set());
  const [nodeLoadingMore, setNodeLoadingMore] = useState<Set<number>>(() => new Set());

  // Search (flat) mode state. `null` = browse mode (search box empty).
  const [searchResults, setSearchResults] = useState<Category[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchingMore, setSearchingMore] = useState(false);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const searchSeq = useRef(0);
  /** In-flight search request - cancelled when a newer keystroke supersedes it. */
  const searchRequestRef = useRef<CancelablePromise<MarketplaceCategoryListView> | null>(null);

  const fetchCategories = (opts: { q?: string; parentKCategory?: number; offset?: number }) =>
    fetchPage
      ? fetchPage({ ...opts, q: opts.q?.trim() || undefined })
      : MarketplaceListingService.getAppMarketplaceListingCategories(
          source,
          opts.q?.trim() || undefined,
          opts.parentKCategory,
          LIMIT,
          opts.offset,
          dataSourceId?.trim() || undefined
        );

  const errorMessage = (e: unknown, fallback: string): string =>
    typeof e === 'string' ? e : t('error_unexpected', { defaultValue: fallback });

  /** Localized message for an assign-category failure (`mp_error.<code>`). */
  const assignErrorMessage = (e: unknown): string => {
    if (e instanceof ApiError) {
      const body = e.body as { error?: string; errorCode?: string | null } | undefined;
      const code = body?.errorCode;
      if (typeof code === 'string' && ASSIGN_ERROR_CODES.has(code)) {
        return t(`mp_error.${code}`);
      }
      if (typeof body?.error === 'string' && body.error.length > 0) {
        return body.error;
      }
    }
    return errorMessage(e, 'Category assignment failed');
  };

  // Load the root level once per channel; reset the tree on source change.
  useEffect(() => {
    if (!source) {
      return;
    }
    let active = true;
    setRoots([]);
    setRootsHasMore(false);
    setChildrenByParent({});
    setChildHasMore({});
    setExpanded(new Set());
    setNodeLoading(new Set());
    setNodeLoadingMore(new Set());
    setRootsLoading(true);
    setError(null);
    fetchCategories({ parentKCategory: ROOT_PARENT, offset: 0 })
      .then((res) => {
        if (!active) {
          return;
        }
        setRoots(res.categories ?? []);
        setRootsHasMore(res.hasMore === true);
        setRootsLoading(false);
      })
      .catch((e) => {
        if (!active) {
          return;
        }
        setRoots([]);
        setError(errorMessage(e, 'Failed to load categories'));
        setRootsLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, dataSourceId]);

  // Debounced flat search; empty term switches back to browse (tree) mode.
  useEffect(() => {
    const term = query.trim();
    if (!term || !source) {
      setSearchResults(null);
      setSearching(false);
      setSearchHasMore(false);
      return;
    }
    const seq = ++searchSeq.current;
    setSearching(true);
    setError(null);
    const timer = setTimeout(() => {
      // Abort any still-running search before firing the new one.
      searchRequestRef.current?.cancel();
      const req = fetchCategories({ q: term, offset: 0 });
      searchRequestRef.current = req;
      req
        .then((res) => {
          if (seq !== searchSeq.current) {
            return;
          }
          setSearchResults(res.categories ?? []);
          setSearchHasMore(res.hasMore === true);
          setSearching(false);
        })
        .catch((e) => {
          if (e instanceof CancelError || seq !== searchSeq.current) {
            return;
          }
          setSearchResults([]);
          setSearchHasMore(false);
          setError(errorMessage(e, 'Category search failed'));
          setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      searchRequestRef.current?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, source, dataSourceId]);

  const toggleExpand = (cat: Category) => {
    const k = cat.kCategory;
    if (typeof k !== 'number') {
      return;
    }
    const willExpand = !expanded.has(k);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(k)) {
        next.delete(k);
      } else {
        next.add(k);
      }
      return next;
    });
    if (willExpand && childrenByParent[k] === undefined && !nodeLoading.has(k)) {
      setNodeLoading((prev) => new Set(prev).add(k));
      fetchCategories({ parentKCategory: k, offset: 0 })
        .then((res) => {
          setChildrenByParent((prev) => ({ ...prev, [k]: res.categories ?? [] }));
          setChildHasMore((prev) => ({ ...prev, [k]: res.hasMore === true }));
        })
        .catch((e) => {
          setError(errorMessage(e, 'Failed to load subcategories'));
        })
        .finally(() => {
          setNodeLoading((prev) => {
            const next = new Set(prev);
            next.delete(k);
            return next;
          });
        });
    }
  };

  const loadMoreRoots = () => {
    if (rootsLoadingMore || !rootsHasMore) {
      return;
    }
    setRootsLoadingMore(true);
    fetchCategories({ parentKCategory: ROOT_PARENT, offset: roots.length })
      .then((res) => {
        setRoots((prev) => [...prev, ...(res.categories ?? [])]);
        setRootsHasMore(res.hasMore === true);
      })
      .catch((e) => {
        setError(errorMessage(e, 'Failed to load categories'));
      })
      .finally(() => {
        setRootsLoadingMore(false);
      });
  };

  const loadMoreChildren = (k: number) => {
    if (nodeLoadingMore.has(k) || childHasMore[k] !== true) {
      return;
    }
    setNodeLoadingMore((prev) => new Set(prev).add(k));
    fetchCategories({ parentKCategory: k, offset: (childrenByParent[k] ?? []).length })
      .then((res) => {
        setChildrenByParent((prev) => ({
          ...prev,
          [k]: [...(prev[k] ?? []), ...(res.categories ?? [])],
        }));
        setChildHasMore((prev) => ({ ...prev, [k]: res.hasMore === true }));
      })
      .catch((e) => {
        setError(errorMessage(e, 'Failed to load subcategories'));
      })
      .finally(() => {
        setNodeLoadingMore((prev) => {
          const next = new Set(prev);
          next.delete(k);
          return next;
        });
      });
  };

  const loadMoreSearch = () => {
    const term = query.trim();
    if (searchingMore || !searchHasMore || !term || searchResults == null) {
      return;
    }
    const seq = searchSeq.current;
    setSearchingMore(true);
    fetchCategories({ q: term, offset: searchResults.length })
      .then((res) => {
        if (seq !== searchSeq.current) {
          return;
        }
        setSearchResults((prev) => [...(prev ?? []), ...(res.categories ?? [])]);
        setSearchHasMore(res.hasMore === true);
      })
      .catch((e) => {
        if (seq === searchSeq.current) {
          setError(errorMessage(e, 'Category search failed'));
        }
      })
      .finally(() => {
        if (seq === searchSeq.current) {
          setSearchingMore(false);
        }
      });
  };

  // Infinite scroll for the outer list: search results in search mode, roots in
  // browse mode (expanded children paginate via their own "Load more" button).
  const handleListScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - SCROLL_THRESHOLD_PX) {
      return;
    }
    if (searchResults !== null) {
      loadMoreSearch();
    } else {
      loadMoreRoots();
    }
  };

  /**
   * "Set category" click: assign immediately when the confirmation was
   * dismissed for good, otherwise toggle the inline popconfirm (✓/✕ with a
   * "don't ask again" checkbox) anchored to the pressed button.
   */
  const handleSetClick = (cat: Category) => {
    if (typeof cat.kCategory !== 'number') {
      return;
    }
    if (isSelectMode) {
      // Pure selection - nothing is written anywhere, no confirmation needed.
      onSelect?.(cat.kCategory, cat.name ?? `#${cat.kCategory}`);
      return;
    }
    if (isSetCategoryConfirmDismissed()) {
      handleAssign(cat);
      return;
    }
    const k = cat.kCategory;
    // Reset the checkbox on every open so a stale check never leaks over.
    setConfirmDontAsk(false);
    // Second click on the same button toggles the popconfirm off.
    setConfirmKey((prev) => (prev === k ? null : k));
  };

  const handleAssign = (cat: Category) => {
    if (typeof cat.kCategory !== 'number' || typeof wawiArtikelId !== 'number') {
      return;
    }
    // Narrowed copy: the guard above does not survive into the .then closure.
    const articleId = wawiArtikelId;
    const kCategory = cat.kCategory;
    setAssigningKey(kCategory);
    setError(null);
    MarketplaceListingService.postAppMarketplaceListingAssignCategory({
      source,
      wawiArtikelId: articleId,
      kCategory,
      dataSourceId: dataSourceId?.trim() || undefined,
    })
      .then(() => {
        // 200 guarantees the attribute schema is already in Wawi (fetched
        // synchronously from the marketplace when it was missing) - the
        // caller can re-request the product fields right away.
        dispatch(
          addNotification({
            type: 'success',
            title: t('notification_success'),
            message: t('marketplace_listing_category_assign_success', {
              name: cat.name ?? `#${kCategory}`,
            }),
          })
        );
        // Keep the cached product list in sync (list badge + the bulk
        // generate modal's category gate) without a full list refetch.
        dispatch(
          applyContentMasterListItemMarketplaceCategory({
            wawiArtikelId: articleId,
            kCategory,
            categoryName: cat.name ?? '',
          })
        );
        onAssigned?.(kCategory, cat.name ?? '');
      })
      .catch((e) => {
        // The category was NOT assigned (no partial state) - keep the picker
        // open with the specific message so the user can retry.
        setError(assignErrorMessage(e));
      })
      .finally(() => {
        setAssigningKey(null);
      });
  };

  /**
   * Row body: category name (+ parent path in search) and a hover-revealed
   * "Set category" button on the right. Nodes with `listingAllowed: false`
   * (non-listable tree parents on Mirakl channels) are muted and NOT
   * selectable - they exist only to expand towards listable leaves, matching
   * the native JTL-Wawi dialog. Every listable category is assignable; the
   * attribute-schema state is surfaced as a badge (see
   * `MarketplaceCategoryListView.attributesImported`): schema not imported yet
   * → amber badge (assigning fetches the schema from the marketplace
   * synchronously - T7, expect the request to take a couple of seconds);
   * really has no attributes → grey badge.
   */
  const renderRowContent = (cat: Category, withPath: boolean) => {
    const attributesNotImported = cat.attributesImported === false;
    const noAttributes = cat.attributesImported === true && (cat.attributeCount ?? 0) === 0;
    // Only an explicit false blocks selection: Wawi categories (Content Master
    // fetchPage override) have no listingAllowed field at all.
    const notListable = cat.listingAllowed === false;
    const k = cat.kCategory;
    return (
      <>
        <span
          className={
            notListable
              ? 'marketplace-category-picker__name-wrap marketplace-category-picker__name-wrap--muted'
              : 'marketplace-category-picker__name-wrap'
          }
        >
          <span className="marketplace-category-picker__name">{cat.name || `#${k}`}</span>
          {attributesNotImported ? (
            <span
              className="marketplace-category-picker__badge marketplace-category-picker__badge--pending"
              title={t('marketplace_listing_category_attrs_not_imported_tooltip')}
            >
              {t('marketplace_listing_category_attrs_not_imported')}
            </span>
          ) : null}
          {noAttributes ? (
            <span className="marketplace-category-picker__badge marketplace-category-picker__badge--empty">
              {t('marketplace_listing_category_attrs_none')}
            </span>
          ) : null}
          {withPath && cat.parentPath ? (
            <span className="marketplace-category-picker__path">{cat.parentPath}</span>
          ) : null}
        </span>
        <span className="marketplace-category-picker__set-wrap marketplace-sync-popconfirm-anchor">
          <button
            type="button"
            className="marketplace-category-picker__set"
            onClick={() => handleSetClick(cat)}
            disabled={
              notListable ||
              typeof k !== 'number' ||
              (!isSelectMode && (assigningKey !== null || !canCmWawiWrite))
            }
            title={
              notListable
                ? t('marketplace_listing_category_not_listable_tooltip')
                : !isSelectMode && !canCmWawiWrite
                ? t('subuser_no_permission_tooltip')
                : undefined
            }
          >
            {isSelectMode
              ? t('content_master_filter_category_select')
              : assigningKey === k
              ? t('loading_text')
              : t('marketplace_listing_category_set')}
          </button>
          {confirmKey === k && typeof k === 'number' ? (
            <MarketplaceSyncPopconfirm
              className="marketplace-sync-popconfirm--stacked"
              onRequestClose={() => setConfirmKey(null)}
            >
              <span className="marketplace-sync-popconfirm__text">
                {t('marketplace_listing_category_set_confirm')}
              </span>
              <label className="marketplace-sync-popconfirm__dont-ask">
                <input
                  type="checkbox"
                  checked={confirmDontAsk}
                  onChange={(e) => setConfirmDontAsk(e.target.checked)}
                />
                <span>{t('content_master_apply_warning_dont_show_again')}</span>
              </label>
              <span className="marketplace-sync-popconfirm__actions">
                <button
                  type="button"
                  className="marketplace-sync-popconfirm__yes"
                  aria-label={t('yes')}
                  title={t('yes')}
                  onClick={() => {
                    if (confirmDontAsk) {
                      persistSetCategoryConfirmDismissed();
                    }
                    setConfirmKey(null);
                    handleAssign(cat);
                  }}
                >
                  ✓
                </button>
                <button
                  type="button"
                  className="marketplace-sync-popconfirm__no"
                  aria-label={t('no')}
                  title={t('no')}
                  onClick={() => setConfirmKey(null)}
                >
                  ✕
                </button>
              </span>
            </MarketplaceSyncPopconfirm>
          ) : null}
        </span>
      </>
    );
  };

  const renderNode = (cat: Category, withPath = false): React.ReactNode => {
    const k = cat.kCategory;
    const isExpanded = typeof k === 'number' && expanded.has(k);
    const isLoadingChildren = typeof k === 'number' && nodeLoading.has(k);
    const isLoadingMore = typeof k === 'number' && nodeLoadingMore.has(k);
    const kids = typeof k === 'number' ? childrenByParent[k] : undefined;
    const hasMoreKids = typeof k === 'number' && childHasMore[k] === true;
    return (
      <li key={k ?? cat.cCategoryId} className="marketplace-category-picker__item">
        <div className="marketplace-category-picker__row">
          {cat.hasChildren ? (
            <button
              type="button"
              className="marketplace-category-picker__toggle"
              onClick={() => toggleExpand(cat)}
              aria-expanded={isExpanded}
              aria-label={t('marketplace_listing_category_expand')}
            >
              {isExpanded ? '▾' : '▸'}
            </button>
          ) : (
            <span
              className="marketplace-category-picker__toggle marketplace-category-picker__toggle--leaf"
              aria-hidden
            />
          )}
          {renderRowContent(cat, withPath)}
        </div>
        {isExpanded ? (
          <ul className="marketplace-category-picker__children">
            {isLoadingChildren ? (
              <li className="marketplace-category-picker__hint">
                {t('marketplace_listing_category_search_loading')}
              </li>
            ) : kids && kids.length > 0 ? (
              <>
                {kids.map((child) => renderNode(child))}
                {isLoadingMore ? (
                  <li className="marketplace-category-picker__hint">
                    {t('marketplace_listing_category_search_loading')}
                  </li>
                ) : hasMoreKids && typeof k === 'number' ? (
                  <li>
                    <button
                      type="button"
                      className="marketplace-category-picker__load-more"
                      onClick={() => loadMoreChildren(k)}
                    >
                      {t('marketplace_listing_category_load_more')}
                    </button>
                  </li>
                ) : null}
              </>
            ) : (
              <li className="marketplace-category-picker__hint">
                {t('marketplace_listing_category_search_empty')}
              </li>
            )}
          </ul>
        ) : null}
      </li>
    );
  };

  // Drive the mode by the input text (not by loaded results) so the browse
  // tree disappears and the loading state shows the instant the user types.
  const isSearchMode = query.trim() !== '';

  return (
    <div className="marketplace-category-picker">
      <input
        type="text"
        className="marketplace-category-picker__input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('marketplace_listing_category_search_placeholder')}
      />
      {error ? <div className="marketplace-category-picker__error">{error}</div> : null}

      {isSearchMode ? (
        searching ? (
          <div className="marketplace-category-picker__hint">
            {t('marketplace_listing_category_search_loading')}
          </div>
        ) : (searchResults?.length ?? 0) === 0 ? (
          <div className="marketplace-category-picker__hint">
            {t('marketplace_listing_category_search_empty')}
          </div>
        ) : (
          <ul className="marketplace-category-picker__list" onScroll={handleListScroll}>
            {searchResults!.map((cat) => renderNode(cat, true))}
            {searchingMore ? (
              <li className="marketplace-category-picker__hint">
                {t('marketplace_listing_category_search_loading')}
              </li>
            ) : null}
          </ul>
        )
      ) : rootsLoading ? (
        <div className="marketplace-category-picker__hint">
          {t('marketplace_listing_category_search_loading')}
        </div>
      ) : roots.length === 0 ? (
        <div className="marketplace-category-picker__hint">
          {t('marketplace_listing_category_search_empty')}
        </div>
      ) : (
        <ul className="marketplace-category-picker__list" onScroll={handleListScroll}>
          {roots.map((cat) => renderNode(cat))}
          {rootsLoadingMore ? (
            <li className="marketplace-category-picker__hint">
              {t('marketplace_listing_category_search_loading')}
            </li>
          ) : null}
        </ul>
      )}
    </div>
  );
};

export default MarketplaceCategoryPicker;
